/* Donation project resolver hooks (customer PWA).
 *
 * Plan §"Donation projects":
 *   GET /donation-projects/by-code/:code   — resolve single project (public).
 *   GET /donation-projects                 — list active projects + canonical
 *                                            `collectedAmountMinor` for the
 *                                            project picker (public).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { LocalizedNameMap } from 'pi-kiosk-shared';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';

export interface DonationProjectDTO {
  id: string;
  code: string;
  name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  nameLocales?: LocalizedNameMap | null;
  description: string | null;
  defaultAmountMinor: number | null;
  currency: string;
  active: boolean;
  goalAmountMinor?: number | null;
  imageUrl?: string | null;
  thumbnailUrl?: string | null;
}

export interface DonationProjectWithCollectedDTO extends DonationProjectDTO {
  /**
   * Canonical net collected amount in minor units (server-computed from
   * `TenantCommerceFact` per plan §6.5). Frontend MUST NOT recompute.
   */
  collectedAmountMinor: number;
}

export interface WhicheverDonationPreviewDTO {
  readonly configured: boolean;
  readonly project: DonationProjectWithCollectedDTO | null;
}

export function useDonationProjectByCode(
  code: string | null
): UseQueryResult<DonationProjectDTO, Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.donationProject(tenantCode, code ?? ''),
    queryFn: () =>
      api.request<DonationProjectDTO>(
        `/donation-projects/by-code/${encodeURIComponent(code ?? '')}`
      ),
    enabled: code !== null && code.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useListDonationProjectsWithCollected(): UseQueryResult<
  DonationProjectWithCollectedDTO[],
  Error
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.donationProjectsList(tenantCode),
    queryFn: () =>
      api.request<DonationProjectWithCollectedDTO[]>(`/donation-projects`),
    staleTime: 60_000,
  });
}

export function useWhicheverDonationPreview(): UseQueryResult<
  WhicheverDonationPreviewDTO,
  Error
> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: [...customerQueryKeys.donationProjectsList(tenantCode), 'whichever-preview'] as const,
    queryFn: () =>
      api.request<WhicheverDonationPreviewDTO>(`/donation-projects/whichever-preview`),
    staleTime: 60_000,
  });
}
